import { IExpenseTagToLinkedAccountModel } from '@Shared/models';

import { AccountCapabilitiesCardAllowanceIntervalEnum, EmbeddedCardImageCardFormatEnum } from '../interfaces/CardsGatewayAutogenerated';

export * from '../interfaces/CardsGatewayAutogenerated';

/**
 * An attempt to model this in Typescript:
 *
 * https://cards.emburse.com/api/v1/docs/#card
 */
export interface EmburseCardsCardJson {
  id: string;
  url: string;
  description: string;
  is_virtual: boolean;
  last_four: string;
  state: string;
  assigned_to: {
    id: string;
    url: string;
    email: string;
    code: string | null;
    first_name: string;
    last_name: string;
  } | null;
  category: {
    id: string;
    url: string;
    code?: string;
    name: string;
  } | null;
  department: {
    id: string;
    url: string;
    name: string;
  } | null;
  location: {
    id: string;
    url: string;
    name: string;
  } | null;
  label: {
    id: string;
    url: string;
    name: string;
  } | null;
  allowance: {
    id: string;
    url: string;
    interval: string;
    amount: number;
    transaction_limit: number;
    daily_limit: number | null;
    single_use: boolean | null;
    start_time: string | null;
    end_time: string | null;
    scope: string[];
    balance: number;
    group_scope: string[];
    vendor: string;
  };
  expiration: string;
  billing_address: {
    address_1: string;
    address_2: string;
    city: string;
    state: string;
    zip_code: string;
    postal_code: string;
    country: string;
  } | null;
  shipping_address?: {
    attn: string;
    address_1: string;
    address_2: string;
    city: string;
    state: string;
    zip_code: string;
    postal_code: string;
    country: string;
  } | null;
  created_at: string;
  network: string;
  shared_link?: string;
}

/**
 * This is the interface for the API response for card issuing details. The original implementation for
 * fetching issuing details just proxied through the API response from the Emburse Cards API. In order to
 * maintain backwards compatibility with the mobile client, the API response stays mostly the same and
 * just maps the data from the newer Gateway APIs.
 *
 * All of the fields that are currently omitted from the original type definition are fields
 * that are not being used in either the web or mobile clients and probably won't be added.
 */
export interface LegacyAbacusCardIssuingJsonResponse
  extends Omit<
    EmburseCardsCardJson,
    'url' | 'category' | 'department' | 'location' | 'allowance' | 'assigned_to' | 'billing_address' | 'label' | 'network' | 'is_virtual'
  > {
  shared_link?: string;
  allowance?: Omit<EmburseCardsCardJson['allowance'], 'id' | 'url' | 'single_use' | 'scope' | 'group_scope' | 'vendor'> & {
    scope?: string[];
    balance_resets_at?: string;
    group_scope?: string[];
    vendor?: string;
  };
  assigned_to: Omit<EmburseCardsCardJson['assigned_to'], 'id' | 'code' | 'url'>;
  billing_address: Omit<EmburseCardsCardJson['billing_address'], 'postal_code' | 'country'>;

  // New fields (were not part of the original response)
  created_at_timestamp?: number;
  provider?: string;
  institution_type?: string;
  parent_linked_account_id?: string;
  is_virtual_card?: boolean;
  is_ghost?: boolean;
  shared_json_link?: string;
  default_category_id?: string;
  display_name?: string;
  expense_tags_to_linked_account?: IExpenseTagToLinkedAccountModel[];
}

export type AmexError = {
  code: string;
  message: string;
  detail: string;
};

export interface AmexGatewayValidationError {
  errors: [
    {
      cardUserErrors: [
        {
          errors: [AmexError];
        }
      ];
    }
  ];
}

/**
 * This is the sort order of the interval options from {@link AccountCapabilitiesCardAllowanceIntervalEnum} on the UI (in the card issuing modal).
 * It should be updated if the enum gets new values
 */
export const CAPABILITIES_INTERVAL_ORDER = [
  AccountCapabilitiesCardAllowanceIntervalEnum.Continuous,
  AccountCapabilitiesCardAllowanceIntervalEnum.Daily,
  AccountCapabilitiesCardAllowanceIntervalEnum.Weekly,
  AccountCapabilitiesCardAllowanceIntervalEnum.Monthly
];

/**
 * The actual logical card type.
 * For the display version, see {@link CARD_TYPES}
 */
export enum CardType {
  Physical = 'physical',
  Ghost = 'ghost',
  Virtual = 'virtual'
}

export const CreateCardOptions = {
  ...CardType,
  BulkOrder: 'bulk'
};
const CreateCardValues = Object.values(CreateCardOptions);
export type CreateCardType = typeof CreateCardValues[number];

/**
 * The card type that users see (they do not distinguish between virtual and ghost cards).
 * For the logical version, see {@link CardType}
 */
export type CARD_TYPES = 'PHYSICAL' | 'DIGITAL';

/**
 * The card type that users see (they do not distinguish between virtual and ghost cards).
 * This is just a map of the values in the corresponding TS type ({@link CARD_TYPES}).
 * For the logical version, see {@link CardType}
 */
export const GATEWAY_EMBURSE_CARD_TYPE: Record<string, CARD_TYPES> = {
  PHYSICAL: 'PHYSICAL',
  DIGITAL: 'DIGITAL'
};

/**
 * The response from the image API with cardFormat={@link EmbeddedCardImageCardFormatEnum.Json}
 * The OpenAPI generator doesn't seem to generate a type/interface for this
 */
export interface CardsGatewayJsonImageResponse {
  pan: string;
  cvv: string;
}

/**
 * The include options supported by the issuing_details endpoint
 */
export enum AbacusIssuingDetailsInclude {
  shared_link = 'shared_link',
  full_digital_shared_link = 'full_digital_shared_link',
  json_shared_link = 'json_shared_link',
  expense_tags = 'expense_tags'
}

/**
 * Short form display names for the card allowance intervals in {@link AccountCapabilitiesCardAllowanceIntervalEnum}.
 * For longer versions, see {@link INTERVAL_LONG_DISPLAY_NAMES}
 */
export const INTERVAL_SHORT_DISPLAY_NAMES = {
  [AccountCapabilitiesCardAllowanceIntervalEnum.Daily]: 'daily',
  [AccountCapabilitiesCardAllowanceIntervalEnum.Weekly]: 'weekly',
  [AccountCapabilitiesCardAllowanceIntervalEnum.Monthly]: 'monthly',
  [AccountCapabilitiesCardAllowanceIntervalEnum.Continuous]: 'non-recurring'
};

/**
 * Long form display names for the card allowance intervals in {@link AccountCapabilitiesCardAllowanceIntervalEnum}.
 * For shorter versions, see {@link INTERVAL_SHORT_DISPLAY_NAMES}
 */
export const INTERVAL_LONG_DISPLAY_NAMES = {
  [AccountCapabilitiesCardAllowanceIntervalEnum.Daily]: 'daily recurring',
  [AccountCapabilitiesCardAllowanceIntervalEnum.Weekly]: 'weekly recurring',
  [AccountCapabilitiesCardAllowanceIntervalEnum.Monthly]: 'monthly recurring',
  [AccountCapabilitiesCardAllowanceIntervalEnum.Continuous]: 'non-recurring'
};

export type CardsGatewayIssuingModes = 'new' | 'edit' | 'duplicate' | 'template';
